import { type CSSProperties, useMemo } from 'react';

import classNames from 'classnames';

import Serializer from './components/Serializer';

import { findFirstImageSrc } from './utils/findFirstImageSrc';
import { processPostTheme } from './utils/processPostTheme';
import { processPostTOC } from './utils/processPostTOC';

import { postContentElement } from './PostContent.css';
import type { PostContentElementProps } from './PostContent.types';
import { PostContentProvider } from './provider';
import {
  overridePostThemeFontFamily,
  postThemeToCSSVariables,
  subscriberBreakCSSVariables,
  webThemeToCSSVariables,
} from './utils';

export function PostContentElement(props: PostContentElementProps) {
  const {
    id,
    isLive,
    isWeb,
    element,
    attributes,
    postTheme,
    webTheme,
    translations,
    homePath,
    loginPath,
    subscribePath,
    upgradePath,
    showSubscriberBreak,
    showPaywallBreak,
    ChildDecoratorComponent,
    isIndexingDisabled,
    rewriteImageSrc,
    utmParams,
    isSubscriberAuthenticated,
    children,
  } = props;

  const { maxWidth, fontFamily } = element.attrs || {};
  const serializerContent = processPostTOC(element.attrs.data || {});
  const eagerImageSrc = useMemo(() => findFirstImageSrc(serializerContent), [serializerContent]);

  return (
    <PostContentProvider utmParams={utmParams} postTheme={postTheme} eagerImageSrc={eagerImageSrc}>
      <div
        {...attributes}
        className={classNames(postContentElement, !isLive && 'pointer-events-none', 'post-content-node')}
        id={id}
        style={
          {
            '--max-width': maxWidth,
            width: '100%',
            ...postThemeToCSSVariables(overridePostThemeFontFamily(processPostTheme(postTheme), fontFamily), { isWeb }),
            ...(webTheme ? { ...webThemeToCSSVariables(webTheme), ...subscriberBreakCSSVariables(webTheme) } : {}),
            '--post-content-inline-link-color': element.attrs.linkColor,
          } as CSSProperties
        }
      >
        <Serializer
          isLive={isLive}
          translations={translations}
          content={serializerContent}
          homePath={homePath}
          loginPath={loginPath}
          subscribePath={subscribePath}
          showSubscriberBreak={showSubscriberBreak}
          showPaywallBreak={showPaywallBreak}
          ChildDecoratorComponent={ChildDecoratorComponent}
          upgradePath={upgradePath}
          isIndexingDisabled={isIndexingDisabled}
          rewriteImageSrc={rewriteImageSrc}
          isSubscriberAuthenticated={isSubscriberAuthenticated}
        />
        {children}
      </div>
    </PostContentProvider>
  );
}
